interface AdjustAttribution {
  tracker_token?: string | null;
  tracker_name?: string | null;
}

interface AdjustSDK {
  initSdk: (config: Record<string, unknown>) => void;
  trackEvent: (config: {
    eventToken: string;
    callbackParams?: Array<{ key: string; value: string }>;
  }) => void;
  waitForAttribution: () => Promise<AdjustAttribution | null>;
  waitForWebUUID: () => Promise<string>;
}

let adjustModulePromise: Promise<AdjustSDK> | null = null;
let adjustInitPromise: Promise<AdjustSDK> | null = null;
const isDevelopment = process.env.NODE_ENV === 'development';

const getAdjustModule = () => {
  if (!adjustModulePromise) {
    adjustModulePromise = import('@adjustcom/adjust-web-sdk').then(
      (module) => module.default as unknown as AdjustSDK
    );
  }

  return adjustModulePromise;
};

export const initAdjust = () => {
  if (typeof window === 'undefined') return;

  if (!adjustInitPromise) {
    adjustInitPromise = getAdjustModule().then((Adjust) => {
      if (isDevelopment) {
        console.log('Adjust env:', 'sandbox');
      }

      Adjust.initSdk({
        appToken: process.env.NEXT_PUBLIC_ADJUST_APP_CASHFREE_TOKEN || '',
        environment: (process.env.NODE_ENV as string) === 'production' ? 'production' : 'sandbox',
        logLevel: (process.env.NODE_ENV as string) === 'production' ? 'error' : 'verbose',
      });

      if (isDevelopment) {
        void Adjust.waitForAttribution().then((attribution) => {
          console.log('Adjust Attribution Data:', JSON.stringify(attribution, undefined, 2));
        });

        void Adjust.waitForWebUUID().then((webUuid) => {
          console.log('Adjust Web UUID:', webUuid);
        });
      }

      return Adjust;
    });
  }

  return adjustInitPromise;
};

export const trackEvent = async (eventToken: string, params?: Record<string, string>) => {
  if (typeof window === 'undefined') return;

  const Adjust = await (initAdjust() ?? getAdjustModule());
  const eventConfig: {
    eventToken: string;
    callbackParams?: Array<{ key: string; value: string }>;
  } = { eventToken };

  if (params) {
    eventConfig.callbackParams = Object.entries(params).map(([key, value]) => ({ key, value }));
  }

  Adjust.trackEvent(eventConfig);
  if (isDevelopment) {
    console.log('Adjust Event Tracked:', eventToken, params);
  }
};

export const getAdjustAttribution = async () => {
  try {
    if (typeof window === 'undefined') return null;
    const Adjust = await (initAdjust() ?? getAdjustModule());
    const attribution = await Adjust.waitForAttribution();

    if (isDevelopment) {
      console.log('Adjust Attribution:', attribution);
    }

    return attribution;
  } catch (error) {
    if (isDevelopment) {
      console.error('Failed to get Adjust attribution:', error);
    }
    return null;
  }
};

export const getAdjustWebUUID = async (): Promise<string | null> => {
  try {
    if (typeof window === 'undefined') return null;
    const Adjust = await (initAdjust() ?? getAdjustModule());
    const webUuid = await Adjust.waitForWebUUID();

    if (isDevelopment) {
      console.log('Adjust Web UUID:', webUuid);
    }

    return webUuid;
  } catch (error) {
    if (isDevelopment) {
      console.error('Failed to get Adjust Web UUID:', error);
    }
    return null;
  }
};

export const getAdjustCampaignId = async (): Promise<string | null> => {
  try {
    const attribution = await getAdjustAttribution();
    return attribution?.tracker_name || null;
  } catch (error) {
    if (isDevelopment) {
      console.error('Failed to get Adjust campaign ID:', error);
    }
    return null;
  }
};
